import { highlightGroup, finishDrop } from '@/graph_parts/moving'
import { isHoveredOver, handleMouseHover, handleMouseLeave } from '@/graph_parts/common_template_support'
import { positionBinding, bindingFromHighlight, bindingPositiveNegative } from '@/graph_parts/binding'
import { validationNotEmpty } from '@/graph_parts/validation'
import SquareLayout from '@/graph_parts/square_layout'
import VerticalLayout from '@/graph_parts/vertical_layout'
import HorizontalLayout from '@/graph_parts/horizontal_layout'
import InlineLayout from '@/graph_parts/inline_layout'
import { orderByOrdering } from '@/utils/ordering'
import { emitterSingleton } from '@/graph_parts/emitter'
import { amber900, orangeA100, grey50, grey200, grey400, grey800, lightBlue50, lightBlue500, blueGrey200, blueGrey600, defaultNodeAttributes } from '@/utils/constants'
import { uuid } from '@/utils/uuid'
import * as go from 'gojs'

var $ = go.GraphObject.make

const determineLayout = function (gridLayout, layout) {
  const isVertical = layout === 'vertical'
  let layoutClass = null
  if (gridLayout === 'inline') {
    layoutClass = InlineLayout
  } else {
    if (typeof layout === 'undefined' || layout === 'flexible' || layout === 'square') { // flexible is deprecated
      return $(SquareLayout,
        {
          cellSize: new go.Size(1, 1),
          spacing: new go.Size(4, 4),
          comparer: orderByOrdering,
          alignment: go.GridLayout.Position,
          sorting: go.GridLayout.Descending
        }
      )
    }
    if (layout === 'vertical') {
      layoutClass = VerticalLayout
    } else if (layout === 'horizontal') {
      layoutClass = HorizontalLayout
    } else {
      layoutClass = HorizontalLayout
    }
  }

  return $(layoutClass, {
    wrappingColumn: isVertical ? 1 : NaN,
    cellSize: new go.Size(1, 1),
    comparer: orderByOrdering,
    sorting: go.GridLayout.Ascending,
    spacing: new go.Size(0, 6),
    alignment: go.GridLayout.Position
  })
}

let myEmitter = emitterSingleton.instance

go.Shape.defineFigureGenerator('ArrowUp', function (shape, w, h) {
  return go.Geometry.parse('M.287.413 3.5 3.619l3.213-3.206L7.7 1.4l-4.2 4.2-4.2-4.2L.287.413z', true)
})

go.Shape.defineFigureGenerator('ArrowDown', function (shape, w, h) {
  return go.Geometry.parse('M.287 5.187 3.5 1.981l3.213 3.206L7.7 4.2l-4.2-4.2-4.2 4.2z', true)
})

function handleSubGraphExpandedChanged (group) {
  group.invalidateLayout()
  if (group.containingGroup) {
    group.containingGroup.invalidateLayout()
  }
}

function showGroupBanner (group) {
  if (group.data.gridLayout === 'inline') {
    if (group.part.memberParts.length <= 0) {
      return true
    }
    let hasGroup = false
    group.part.memberParts.each(x => {
      if (x.data.isGroup) {
        hasGroup = true
      }
    })
    return !hasGroup
  }
  return true
}

function createGroupTemplate (emitter, isTester) {
  myEmitter.emitter = emitter
  const font = 'normal 16px Roboto'
  return $(go.Group, 'Spot',
    {
      mouseDragEnter: function (e, grp, prev) { highlightGroup(e, grp, true) },
      mouseDragLeave: function (e, grp, next) { highlightGroup(e, grp, false) },
      mouseDrop: finishDrop,
      computesBoundsAfterDrag: true,
      computesBoundsIncludingLocation: true,
      handlesDragDropForMembers: true,
      shadowBlur: 8,
      shadowColor: '#5e6d76',
      cursor: 'pointer',
      selectionAdornmentTemplate: // Hide the default blue border when group is selected
        $(go.Adornment, 'Auto',
          $(go.Shape, 'Rectangle',
            { fill: null, stroke: null }),
          $(go.Placeholder)
        ),
      subGraphExpandedChanged: handleSubGraphExpandedChanged
    },
    new go.Binding('layout', '', (x) => {
      return determineLayout(x.gridLayout, x.layout)
    }),
    new go.Binding('isShadowed', '', i => { return i.data.isInPalette === false && i.isTopLevel }).ofObject(),
    positionBinding(),
    new go.Binding('isSubGraphExpanded', 'isSubGraphExpanded').makeTwoWay(),
    new go.Binding('opacity', '', x => x.isIncludedInQuery && !x.isParentExcludedInQuery ? 1 : 0.4),
    $(go.Panel, 'Auto', // Main panel
      {
        stretch: go.GraphObject.Horizontal,
        mouseHover: handleMouseHover,
        mouseLeave: handleMouseLeave
      },
      $(go.Shape, 'RoundedRectangle', // Main panel shape
        {
          parameter1: 3,
          stroke: grey200,
          strokeWidth: 2,
          fill: grey200
        },
        new go.Binding('stroke', '', x => { return isHoveredOver(x) ? amber900 : grey200 }),
        bindingPositiveNegative('fill', grey200, grey800)
      ),
      $(go.Panel, 'Vertical', // Stack title bar above elements
        {
          margin: 0
        },
        $(go.Panel, 'Table', // Title bar panel
          {
            defaultAlignment: go.Spot.Left,
            stretch: go.GraphObject.Horizontal,
            margin: 0
          },
          new go.Binding('visible', '', i => showGroupBanner(i)).ofObject(),
          $(go.Panel, 'Auto',
            {
              row: 0,
              column: 0
            },
            $(go.Panel, 'Horizontal',
              $(go.Panel, 'Auto',
                new go.Binding('visible', '', i => (typeof i.fieldSpecifier !== 'undefined' && i.fieldSpecifier.trim() !== '')),
                $(go.Shape, 'RoundedLeftRectangle', {fill: blueGrey600, strokeWidth: 0, parameter1: 2}),
                bindingPositiveNegative('fill', blueGrey600, grey800),
                $(go.TextBlock,
                  {
                    margin: new go.Margin(9, 7, 9, 7),
                    editable: true,
                    isMultiline: false,
                    font: font,
                    stroke: 'white',
                    background: blueGrey600
                  },
                  new go.Binding('text', 'fieldSpecifier').makeTwoWay()
                )
              ),
              $('SubGraphExpanderButton',
                {
                  '_subGraphExpandedFigure': 'TdsUpArrow',
                  '_subGraphCollapsedFigure': 'TdsDownArrow',
                  'ButtonIcon.strokeWidth': 2,
                  'ButtonIcon.desiredSize': new go.Size(10, 5),
                  '_buttonFillNormal': null,
                  'ButtonIcon.fill': blueGrey200,
                  'ButtonIcon.stroke': blueGrey200,
                  '_buttonFillOver': grey50,
                  'ButtonBorder.stroke': null,
                  'ButtonBorder.fill': null,
                  '_buttonStrokeOver': null,
                  'ButtonIcon.margin': 5,
                  margin: 5
                }
              ),
              $(go.Panel, 'Auto',
                $(go.Shape, 'RoundedRightRectangle',
                  {
                    fill: grey200,
                    strokeWidth: 0,
                    parameter1: 2
                  },
                  bindingPositiveNegative('fill', grey200, grey800)
                ),
                $(go.TextBlock,
                  {
                    alignment: go.Spot.Left,
                    editable: true,
                    isMultiline: false,
                    margin: new go.Margin(9, 7, 9, 7),
                    font: font,
                    textValidation: validationNotEmpty,
                    stroke: grey800
                  },
                  new go.Binding('text', 'text').makeTwoWay(),
                  bindingPositiveNegative('stroke', grey800, grey50)
                )
              )
            )
          ),
          $(go.Panel, 'Auto', // Operator panel
            {
              row: 0,
              column: 1,
              alignment: go.Spot.Right,
              stretch: go.GraphObject.Horizontal
            },
            $(go.Shape, 'RoundedRectangle',
              {
                fill: grey200,
                strokeWidth: 0,
                parameter1: 2
              },
              bindingPositiveNegative('fill', grey200, grey800)
            ),
            $(go.TextBlock,
              {
                name: 'EDITABLE_OPERATOR',
                alignment: go.Spot.Right,
                editable: true,
                isMultiline: false,
                margin: new go.Margin(9, 7, 9, 7),
                font: font,
                stroke: grey800,
                textValidation: validationNotEmpty,
                textEdited: function (textBlock, oldText, newText) {
                  const i = textBlock.part.memberParts.iterator
                  while (i.next()) {
                    if (i.value.part.data.isOperator) {
                      textBlock.diagram.model.setDataProperty(i.value.part.data, 'text', newText)
                    }
                  }
                  textBlock.part.invalidateLayout()
                }
              },
              new go.Binding('text', 'operator').makeTwoWay(),
              bindingPositiveNegative('stroke', grey800, grey50)
            )
          )
        ),
        $(go.Panel, 'Auto', // Elements panel
          {
            stretch: go.GraphObject.Horizontal,
            minSize: new go.Size(100, 0),
            margin: 0,
            doubleClick: handleDoubleClick
          },
          $(go.Shape, 'RoundedBottomRectangle',
            {
              parameter1: 3,
              fill: 'white',
              stroke: 'transparent'
            },
            bindingFromHighlight('fill', 'white', lightBlue50, orangeA100, grey800),
            bindingFromHighlight('stroke', 'transparent', lightBlue50, orangeA100, grey800)
          ),
          $(go.Placeholder,
            { padding: new go.Margin(1, 5, 30, 5), alignment: go.Spot.TopLeft, stretch: go.GraphObject.Horizontal }
          ),
          $(go.TextBlock,
            {
              text: 'Double-click to enter a term',
              font: 'italic 12px Roboto',
              stroke: grey400
            },
            new go.Binding('stroke', '', x => { return x.part.memberParts.count <= 0 ? grey800 : 'transparent' }).ofObject()
          )
        )
      )
    ),
    $(go.Panel, 'Auto',
      { alignment: new go.Spot(1, 0, 8, 8), alignmentFocus: go.Spot.BottomRight },
      $(go.Shape,
        { figure: 'RoundedRectangle', fill: lightBlue500, strokeWidth: 1, stroke: 'transparent', parameter1: 5 },
        new go.Binding('fill', '', x => x.data._showingCount ? lightBlue500 : 'transparent').ofObject()
      ),
      $(go.TextBlock,
        {
          isMultiline: false,
          font: 'normal 12px Roboto',
          stroke: 'white'
        },
        new go.Binding('stroke', '', x => x.data._showingCount ? 'white' : 'transparent').ofObject(),
        new go.Binding('text', '', x => { return x.data._showingCount ? x.data._count : '' }).ofObject()
      )
    )
  )
}

function handleDoubleClick (e, part) {
  e.diagram.startTransaction('group_double_click')
  const newNodeData = { key: uuid(), ...defaultNodeAttributes, group: part.part.data.key }
  let ordering = 0
  part.part.memberParts.each(x => {
    if (x.data.ordering > ordering) {
      ordering = x.data.ordering
    }
  })
  ordering++
  newNodeData.ordering = ordering
  e.diagram.animationManager.isEnabled = false
  e.diagram.model.addNodeData(newNodeData)
  e.diagram.commitTransaction('group_double_click')
  const node = e.diagram.findNodeForData(newNodeData)
  e.diagram.commandHandler.editTextBlock(node.elt(0).elt(1).elt(1).elt(1))
  e.diagram.animationManager.isEnabled = true
}

export { createGroupTemplate }



// WEBPACK FOOTER //
// ./src/graph_parts/group_template.js